import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useInviteAssistant } from 'features/group-chat/hooks/use-invite';
import { InvitationPayload } from 'features/group-chat/services/group-services';
import { initialFeaturesState, MessageA2uiSurface } from 'features/chat/types/chat';
import { mapMainStoreMessagesToAgentChat, getActiveStreamStatusEntries } from 'features/chat/utils/agent-main-chat-helpers';
import { useConfig } from 'hooks/use-config';
import { useChatStore } from 'state/store/chat/use-chat-store';
import { useMcpServiceChatStore } from 'state/store/chat/use-mcp-service-chat-store';
import { useCollaborationSocketStore } from 'state/store/collaboration/use-collaboration-store';
type PreviewConversation = {
  id: string;
  type: 'bot' | 'user';
  message: string;
  timestamp?: string;
  a2uiSurfaces?: MessageA2uiSurface[];
  /** Bot generation id (resume `assistantMessageId`). */
  messageId?: string;
  /** Originating user message id (resume `messageId`). */
  userRecordId?: string;
};

type UseAgentPreviewChatOptions = {
  agentId: string;
  widgetId?: string;
};

export function useAgentPreviewChat({ agentId }: UseAgentPreviewChatOptions) {
  const { configurationSettings } = useConfig();
  const inviteAssistantMutation = useInviteAssistant();
  const [conversationId, setConversationId] = useState<string | null>(null);

  const conversationsById = useChatStore((state) => state.conversations);
  const sendMessageToStore = useChatStore((state) => state.sendMessage);
  const fetchAndSetGroupChatParticipants = useChatStore(
    (state) => state.fetchAndSetGroupChatParticipants
  );
  const handleGroupChatNotification = useChatStore((state) => state.handleGroupChatNotification);
  const handleStreamingMessageBatch = useChatStore((state) => state.handleStreamingMessageBatch);
  const streamingMessageBatch = useCollaborationSocketStore(
    (state) => state.streamingMessageBatch
  );

  const conversationFromStore = conversationId ? conversationsById[conversationId] : undefined;
  const storeMessages = conversationFromStore?.messages ?? [];
  const isCurrentResponseGenerating =
    conversationFromStore?.isCurrentResponseGenerating ?? false;
  const streamingAssistantMessageId = conversationFromStore?.streamingAssistantMessageId ?? null;
  const isCurrentMessageLoading = conversationFromStore?.isCurrentMessageLoading ?? false;

  useEffect(() => {
    return () => {
      if (!conversationId) return;

      const { activeConversationId } = useChatStore.getState();
      if (activeConversationId === conversationId) {
        useChatStore.getState().setActiveConversationId(null);
      }
    };
  }, [conversationId]);

  useEffect(() => {
    if (!conversationId || !configurationSettings) return;

    const existingMessageCount =
      useChatStore.getState().conversations[conversationId]?.messages?.length ?? 0;

    fetchAndSetGroupChatParticipants(
      conversationId,
      configurationSettings,
      false,
      existingMessageCount === 0
    );
  }, [conversationId, configurationSettings, fetchAndSetGroupChatParticipants]);

  useEffect(() => {
    if (!configurationSettings || !streamingMessageBatch?.length || !conversationId) return;

    const processBatch = async () => {
      try {
        // Drain atomically from the live store instead of the render closure so
        // rapid streaming batches aren't dropped by a trailing clear (see the
        // collaboration store's drainStreamingBatch for details).
        const batch = useCollaborationSocketStore.getState().drainStreamingBatch();
        if (batch.length === 0) return;

        const userMessages: unknown[] = [];
        const streamingMessages: unknown[] = [];
        const processingStartedMessages: unknown[] = [];

        for (const message of batch) {
          if (!message) continue;

          const batchConversationId =
            message?.response?.conversationId ?? message?.conversationId;

          if (batchConversationId && batchConversationId !== conversationId) {
            continue;
          }

          switch (message.type) {
            case 'USER_MESSAGE':
              if (message?.response?.message?.senderId) {
                userMessages.push(message);
              }
              break;
            case 'ASSISTANT_STREAMED_RESPONSE':
              if (
                message?.streamingResponse?.type === 'ASSISTANT_REQUEST_PROCESSING_STARTED'
              ) {
                processingStartedMessages.push(message);
              } else {
                streamingMessages.push(message);
              }
              break;
            case 'PLATFORM_RESOURCE_CHANGED':
              streamingMessages.push(message);
              break;
            default:
              break;
          }
        }

        for (const msg of userMessages) {
          await handleGroupChatNotification(
            msg as Parameters<typeof handleGroupChatNotification>[0],
            configurationSettings,
            (msg as { type: string }).type
          );
        }

        for (const msg of processingStartedMessages) {
          await handleGroupChatNotification(
            msg as Parameters<typeof handleGroupChatNotification>[0],
            configurationSettings,
            (msg as { streamingResponse: { type: string } }).streamingResponse.type
          );
        }

        if (streamingMessages.length > 0) {
          handleStreamingMessageBatch(
            streamingMessages as Parameters<typeof handleStreamingMessageBatch>[0],
            configurationSettings
          );
        }
      } catch (error) {
        console.error('Error handling agent preview streaming batch:', error);
      }
    };

    void processBatch();
  }, [
    streamingMessageBatch,
    configurationSettings,
    conversationId,
    handleGroupChatNotification,
    handleStreamingMessageBatch,
  ]);

  // MCP service auth resume: after the user connects via McpServiceAuthModal, re-run
  // the paused agent turn under the same generation (mirrors chat.tsx's setResume wiring).
  const lastSentMessageRef = useRef<string>('');
  useEffect(() => {
    if (!configurationSettings) return;
    useMcpServiceChatStore.getState().setResume(async () => {
      await useChatStore.getState().resumePendingAuthTurn(configurationSettings, {
        content: lastSentMessageRef.current,
      });
    });
    return () => useMcpServiceChatStore.getState().setResume(null);
  }, [configurationSettings]);

  const preparePreviewSend = useCallback(
    async (
      trimmed: string,
      targetConversationId: string,
    ) => {
      lastSentMessageRef.current = trimmed;
      await sendMessageToStore(
        trimmed,
        configurationSettings!,
        targetConversationId,
        undefined,
        initialFeaturesState,
        false,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        false,
        undefined,
        [],
        undefined,
        agentId,
        true,
      );
    },
    [agentId, configurationSettings, sendMessageToStore],
  );

  const sendMessage = useCallback(
    async (message: string) => {
      const trimmed = message.trim();
      if (!trimmed || !configurationSettings || !agentId) return;
      if (
        inviteAssistantMutation.isPending ||
        isCurrentResponseGenerating ||
        isCurrentMessageLoading
      ) {
        return;
      }

      try {
        if (!conversationId) {
          const payload: InvitationPayload = {
            invitationType: 'ADD_USER',
            agentId,
            type: 'test',
          };

          const response = await inviteAssistantMutation.mutateAsync(payload);
          if (!response?.conversationId) return;

          const newConversationId = response.conversationId;

          setConversationId(newConversationId);
          await preparePreviewSend(trimmed, newConversationId);
          return;
        }

        await preparePreviewSend(trimmed, conversationId);
      } catch (error) {
        console.error('Agent preview send failed:', error);
      }
    },
    [
      agentId,
      configurationSettings,
      conversationId,
      inviteAssistantMutation,
      isCurrentResponseGenerating,
      isCurrentMessageLoading,
      preparePreviewSend,
    ]
  );

  const agentMessages = useMemo(
    () =>
      mapMainStoreMessagesToAgentChat(
        storeMessages,
        streamingAssistantMessageId,
        isCurrentResponseGenerating
      ),
    [storeMessages, streamingAssistantMessageId, isCurrentResponseGenerating]
  );

  const streamStatusEntries = useMemo(
    () =>
      getActiveStreamStatusEntries(
        storeMessages,
        streamingAssistantMessageId,
        isCurrentResponseGenerating,
      ),
    [storeMessages, streamingAssistantMessageId, isCurrentResponseGenerating],
  );

  const conversations: PreviewConversation[] = useMemo(
    () =>
      agentMessages
        .filter(
          (msg) =>
            msg.type === 'user' ||
            msg.message.trim().length > 0 ||
            (msg.a2uiSurfaces?.length ?? 0) > 0
        )
        .map((msg, index) => ({
          id: `${msg.type}-${index}-${msg.timestamp || index}`,
          type: msg.type,
          message: msg.message,
          timestamp: msg.timestamp || new Date().toISOString(),
          ...(msg.a2uiSurfaces?.length && { a2uiSurfaces: msg.a2uiSurfaces }),
          ...(msg.messageId && { messageId: msg.messageId }),
          ...(msg.userRecordId && { userRecordId: msg.userRecordId }),
        })),
    [agentMessages]
  );

  const lastUserMessageIndex = agentMessages.reduce(
    (lastIndex, msg, index) => (msg.type === 'user' ? index : lastIndex),
    -1,
  );

  const latestBotForActiveTurn = agentMessages
    .slice(lastUserMessageIndex + 1)
    .find((msg) => msg.type === 'bot');

  const hasActiveTurnBotContent = Boolean(latestBotForActiveTurn?.message?.trim());

  const isBotStreaming = agentMessages.some(
    (msg) => msg.type === 'bot' && msg.streaming && msg.message.trim().length > 0,
  );
  const isBotThinking =
    isCurrentResponseGenerating && !hasActiveTurnBotContent && !isBotStreaming;
  const isResponseGenerating =
    isCurrentResponseGenerating || isCurrentMessageLoading;
  const currentEvent = isBotThinking ? 'Sending' : null;
  const isConnected = !!configurationSettings && !!agentId;

  return {
    conversations,
    conversationId,
    suggestions: [] as string[],
    currentEvent,
    streamStatusEntries,
    isBotThinking,
    isBotStreaming,
    isResponseGenerating,
    isConnected,
    sendMessage,
  };
}
